import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
} from "@reduxjs/toolkit/query/react";
import { getUserFromLS } from "../../utils/getUserFromLS";
import {
  getTutorialFixtureEpoch,
  isTutorialFixtureActive,
} from "../../utils/tutorialFixtureState";
import type {
  ApiError,
  Capabilities,
  CompanyDiscoveryCreate,
  CompanyDiscoverySetup,
  CheckAccepted,
  Decisions,
  DecisionsResult,
  Page_Publication_,
  Page_Run_,
  Preview,
  PreviewAccepted,
  PreviewCreate,
  Run,
  Source,
  SourceCreate,
  SourcePage,
  SourcePatch,
  SourceReplace,
  SourceStateWrite,
} from "./contracts.generated";

export type AssetMonitoringScope = {
  clientId: string;
  actorId: string;
  preview?: boolean;
  tutorial?: number;
};
export type MonitoringError = ApiError & { status?: number | string };
type Request = AssetMonitoringScope & {
  path: string;
  method?: string;
  body?: unknown;
  key?: string;
};
type Entity = AssetMonitoringScope & { id: string };
type Write<T> = AssetMonitoringScope & { body: T; key: string };
const transport = fetchBaseQuery({ baseUrl: "", timeout: 30000 });
const baseQuery: BaseQueryFn<Request, unknown, MonitoringError> = async (
  args,
  api,
  extra,
) => {
  if (args.preview || args.tutorial != null) {
    if (
      args.tutorial != null &&
      (!isTutorialFixtureActive() ||
        args.tutorial !== getTutorialFixtureEpoch())
    )
      return {
        error: {
          code: "session_changed",
          message: "The guided tour has ended.",
          status: 401,
        },
      };
    if (args.preview && !import.meta.env.DEV && import.meta.env.MODE !== "test")
      return {
        error: {
          code: "preview_unavailable",
          message: "Development previews are unavailable.",
          status: 403,
        },
      };
    const { fixtureResponse } = await import("./fixtures");
    const result = await fixtureResponse(args);
    if (
      args.tutorial != null &&
      (!isTutorialFixtureActive() ||
        args.tutorial !== getTutorialFixtureEpoch())
    )
      return {
        error: {
          code: "session_changed",
          message: "The guided tour has ended.",
          status: 401,
        },
      };
    // The cache may freeze response values; never expose the fixture store itself.
    return "data" in result ? { data: structuredClone(result.data) } : result;
  }
  const user = getUserFromLS();
  const auth = (
    api.getState() as {
      auth?: { isAuthenticated?: boolean; user?: { id?: string } };
    }
  ).auth;
  if (
    isTutorialFixtureActive() ||
    auth?.isAuthenticated === false ||
    !user.access_token ||
    user.id !== args.actorId ||
    (auth?.user?.id && auth.user.id !== args.actorId)
  )
    return {
      error: {
        code: "session_changed",
        message: "Your session changed. Sign in again to continue.",
        status: 401,
      },
    };
  const result = await transport(
    {
      url: `${import.meta.env.VITE_API_BASE_URL}/clients/${encodeURIComponent(args.clientId)}/asset-monitoring${args.path}`,
      method: args.method ?? "GET",
      body: args.body,
      headers: {
        Authorization: `Bearer ${user.access_token}`,
        ...(args.key ? { "Idempotency-Key": args.key } : {}),
      },
    },
    api,
    extra,
  );
  const latestUser = getUserFromLS();
  const latestAuth = (
    api.getState() as {
      auth?: { isAuthenticated?: boolean; user?: { id?: string } };
    }
  ).auth;
  if (
    isTutorialFixtureActive() ||
    latestAuth?.isAuthenticated === false ||
    latestUser.id !== args.actorId ||
    (latestAuth?.user?.id && latestAuth.user.id !== args.actorId)
  )
    return {
      error: {
        code: "session_changed",
        message: "Your session changed. Sign in again to continue.",
        status: 401,
      },
    };
  if (result.error) {
    const data = result.error.data as Partial<ApiError> | undefined;
    return {
      error: {
        code: data?.code ?? "request_failed",
        message:
          data?.message ??
          (result.error.status === 403
            ? "You do not have permission to manage website sources for this client."
            : "We could not complete this request. Please try again."),
        retryable: data?.retryable,
        details: data?.details,
        status: result.error.status,
      },
    };
  }
  return { data: result.data };
};
export const scopeTag = (scope: AssetMonitoringScope) =>
  `${scope.clientId}:${scope.actorId}:${scope.preview ?? false}:${scope.tutorial ?? "live"}`;
export const monitoringMutationKey = () => crypto.randomUUID();
const monitoringReadTags = (scope: AssetMonitoringScope & { query?: string }) =>
  new URLSearchParams(scope.query).has("cursor")
    ? []
    : [{ type: "Monitoring" as const, id: scopeTag(scope) }];
export function monitoringError(error: unknown): string | null {
  if (!error) return null;
  return typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
    ? error.message
    : "We could not complete this request. Please try again.";
}
export const monitoringAccessError = (error: unknown) =>
  !!error &&
  typeof error === "object" &&
  "status" in error &&
  [401, 403].includes(Number(error.status));
export const assetMonitoringApi = createApi({
  reducerPath: "assetMonitoringApi",
  baseQuery,
  tagTypes: ["Monitoring"],
  keepUnusedDataFor: 30,
  endpoints: (b) => ({
    companySetup: b.query<CompanyDiscoverySetup, AssetMonitoringScope>({
      query: (s) => ({ ...s, path: "/company-discovery" }),
      providesTags: (_, __, s) => monitoringReadTags(s),
    }),
    enableCompanyDiscovery: b.mutation<Source, Write<CompanyDiscoveryCreate>>({
      query: (s) => ({ ...s, path: "/company-discovery", method: "POST" }),
      invalidatesTags: (_, e, s) =>
        e ? [] : [{ type: "Monitoring", id: scopeTag(s) }],
    }),
    status: b.query<Capabilities, AssetMonitoringScope>({
      query: (s) => ({ ...s, path: "/status" }),
      providesTags: (_, __, s) => monitoringReadTags(s),
    }),
    sources: b.query<SourcePage, AssetMonitoringScope & { query?: string }>({
      query: (s) => ({ ...s, path: `/sources${s.query ? `?${s.query}` : ""}` }),
      providesTags: (_, __, s) => monitoringReadTags(s),
    }),
    source: b.query<Source, Entity>({
      query: (s) => ({ ...s, path: `/sources/${encodeURIComponent(s.id)}` }),
      providesTags: (_, __, s) => monitoringReadTags(s),
    }),
    publications: b.query<
      Page_Publication_,
      AssetMonitoringScope & { query?: string }
    >({
      query: (s) => ({
        ...s,
        path: `/publications${s.query ? `?${s.query}` : ""}`,
      }),
      providesTags: (_, __, s) => monitoringReadTags(s),
    }),
    runs: b.query<Page_Run_, Entity & { query?: string }>({
      query: (s) => ({
        ...s,
        path: `/sources/${encodeURIComponent(s.id)}/runs${s.query ? `?${s.query}` : ""}`,
      }),
      providesTags: (_, __, s) => monitoringReadTags(s),
    }),
    run: b.query<Run, Entity>({
      query: (s) => ({ ...s, path: `/runs/${encodeURIComponent(s.id)}` }),
    }),
    preview: b.query<Preview, Entity>({
      query: (s) => ({ ...s, path: `/previews/${encodeURIComponent(s.id)}` }),
    }),
    createPreview: b.mutation<PreviewAccepted, Write<PreviewCreate>>({
      query: (s) => ({ ...s, path: "/previews", method: "POST" }),
    }),
    activateSource: b.mutation<Source, Write<SourceCreate>>({
      query: (s) => ({ ...s, path: "/sources", method: "POST" }),
      invalidatesTags: (_, e, s) =>
        e ? [] : [{ type: "Monitoring", id: scopeTag(s) }],
    }),
    editSource: b.mutation<Source, Write<SourcePatch> & { id: string }>({
      query: (s) => ({ ...s, path: `/sources/${s.id}`, method: "PATCH" }),
      invalidatesTags: (_, e, s) =>
        e ? [] : [{ type: "Monitoring", id: scopeTag(s) }],
    }),
    setSourceState: b.mutation<
      Source,
      Write<SourceStateWrite> & { id: string }
    >({
      query: (s) => ({ ...s, path: `/sources/${s.id}/state`, method: "POST" }),
      invalidatesTags: (_, e, s) =>
        e ? [] : [{ type: "Monitoring", id: scopeTag(s) }],
    }),
    replaceSource: b.mutation<
      Source,
      Write<SourceReplace> & { id: string; restore?: boolean }
    >({
      query: (s) => ({
        ...s,
        path: `/sources/${s.id}/${s.restore ? "restore" : "replace"}`,
        method: "POST",
      }),
      invalidatesTags: (_, e, s) =>
        e ? [] : [{ type: "Monitoring", id: scopeTag(s) }],
    }),
    removeSource: b.mutation<void, Entity & { version: number; key: string }>({
      query: (s) => ({
        ...s,
        path: `/sources/${s.id}?version=${s.version}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, e, s) =>
        e ? [] : [{ type: "Monitoring", id: scopeTag(s) }],
    }),
    checkSource: b.mutation<CheckAccepted, Entity & { key: string }>({
      query: (s) => ({ ...s, path: `/sources/${s.id}/checks`, method: "POST" }),
      invalidatesTags: (_, e, s) =>
        e ? [] : [{ type: "Monitoring", id: scopeTag(s) }],
    }),
    decidePublications: b.mutation<DecisionsResult, Write<Decisions>>({
      query: (s) => ({ ...s, path: "/publications/decisions", method: "POST" }),
      invalidatesTags: (_, e, s) =>
        e ? [] : [{ type: "Monitoring", id: scopeTag(s) }],
    }),
    retryPublication: b.mutation<
      CheckAccepted,
      Entity & { key: string; refresh?: boolean }
    >({
      query: (s) => ({
        ...s,
        path: `/publications/${s.id}/${s.refresh ? "refresh" : "retry"}`,
        method: "POST",
      }),
      invalidatesTags: (_, e, s) =>
        e ? [] : [{ type: "Monitoring", id: scopeTag(s) }],
    }),
  }),
});
